import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { useLocale } from '../context/LocaleContext';
import {
  Calendar, Link2, Unlink, RefreshCw, Loader2,
  CheckCircle, XCircle, ExternalLink, CalendarDays
} from 'lucide-react';

const GoogleCalendarIntegration = () => {
  const { toast } = useToast();
  const { t } = useLocale();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/calendar/google/status`,
        { credentials: 'include' }
      );
      const data = await response.json();
      setStatus(data);

      if (data.connected) {
        loadEvents();
      }
    } catch (error) {
      console.error('Failed to check calendar status:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/calendar/google/events?days=7`,
        { credentials: 'include' }
      );
      const data = await response.json();
      if (data.success) {
        setEvents(data.events || []);
      }
    } catch (error) {
      console.error('Failed to load events:', error);
    }
  };

  const connectCalendar = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/calendar/google/connect`,
        { credentials: 'include' }
      );
      const data = await response.json();
      
      if (data.auth_url) {
        window.location.href = data.auth_url;
      } else {
        toast({
          title: 'Connection Error',
          description: data.message || 'Unable to connect to Google Calendar',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to initiate connection',
        variant: 'destructive'
      });
    }
  };

  const disconnectCalendar = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/calendar/google/disconnect`,
        { method: 'POST', credentials: 'include' }
      );
      
      if (response.ok) {
        toast({ title: 'Disconnected', description: 'Google Calendar has been disconnected' });
        setStatus({ ...status, connected: false });
        setEvents([]);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to disconnect', variant: 'destructive' });
    }
  };

  const syncEvents = async () => {
    setSyncing(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/calendar/google/sync`,
        { method: 'POST', credentials: 'include' }
      );
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: '✅ Sync Complete',
          description: `Synced ${data.synced_count} events from Google Calendar`
        });
        loadEvents();
      }
    } catch (error) {
      toast({ title: 'Sync Failed', description: 'Unable to sync events', variant: 'destructive' });
    } finally {
      setSyncing(false);
    }
  };

  const formatEventTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-purple-500 mx-auto" />
          <p className="text-gray-500 mt-2">Checking calendar connection...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-500" />
              Google Calendar
            </CardTitle>
            <CardDescription>
              Sync your calendar events with LifeFlo
            </CardDescription>
          </div>
          {status?.connected ? (
            <Badge className="bg-green-100 text-green-700">
              <CheckCircle className="h-3 w-3 mr-1" /> Connected
            </Badge>
          ) : (
            <Badge variant="secondary">
              <XCircle className="h-3 w-3 mr-1" /> Not Connected
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!status?.configured ? (
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
            <Calendar className="h-10 w-10 text-blue-400 mx-auto mb-3" />
            <p className="text-blue-700 dark:text-blue-300 text-sm font-medium">
              Google Calendar Integration
            </p>
            <p className="text-blue-600 dark:text-blue-400 text-xs mt-1">
              Coming soon! Stay tuned for calendar sync features.
            </p>
          </div>
        ) : !status?.connected ? (
          <div className="text-center py-6">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">Connect your Google Calendar to sync events</p>
            <Button onClick={connectCalendar}>
              <Link2 className="h-4 w-4 mr-2" /> Connect Google Calendar
            </Button>
          </div>
        ) : (
          <>
            {/* Connected State */}
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-500">
                Your calendar is connected and syncing automatically
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={syncEvents} disabled={syncing}>
                  {syncing ? (
                    <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Syncing...</>
                  ) : (
                    <><RefreshCw className="h-4 w-4 mr-1" /> Sync Now</>
                  )}
                </Button>
                <Button variant="ghost" size="sm" onClick={disconnectCalendar}>
                  <Unlink className="h-4 w-4 mr-1" /> Disconnect
                </Button>
              </div>
            </div>

            {/* Upcoming Events */}
            {events.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-sm text-gray-700">Upcoming Events (7 days)</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {events.slice(0, 10).map((event) => (
                    <div
                      key={event.id}
                      className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <CalendarDays className="h-5 w-5 text-blue-500" />
                        <div>
                          <p className="font-medium text-sm">{event.title}</p>
                          <p className="text-xs text-gray-500">{formatEventTime(event.start)}</p>
                          {event.location && (
                            <p className="text-xs text-gray-400">📍 {event.location}</p>
                          )}
                        </div>
                      </div>
                      {event.link && (
                        <a
                          href={event.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-600"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {events.length === 0 && (
              <div className="text-center py-4 text-gray-500">
                <CalendarDays className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No upcoming events in the next 7 days</p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default GoogleCalendarIntegration;
